import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";

// Accept both snake_case (API-native) and camelCase URL keys so referral
// links from CRMs, embeds, or marketing pages don't have to normalise them.
const READ_PARAM = (params, ...keys) => {
  for (const k of keys) {
    const v = params.get(k);
    if (v !== null && v !== undefined) return v;
  }
  return null;
};

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pull values from the URL exactly once so subsequent typing isn't
  // clobbered by re-renders. `useMemo` on the raw params.toString() keeps
  // this stable across renders while still respecting deep-link changes.
  const prefill = useMemo(() => {
    const p = searchParams;
    const business_name = READ_PARAM(p, "business_name", "business", "businessName") || "";
    const name = READ_PARAM(p, "name", "contact_name", "contactName") || "";
    const email = READ_PARAM(p, "email") || "";
    const phone = READ_PARAM(p, "phone", "mobile") || "";
    // Prefer an explicit `address`; fall back to `city` so referral links
    // that only pass the locality still land somewhere useful. The user
    // can refine before submitting — the backend geocodes anyway.
    const address =
      READ_PARAM(p, "address") || READ_PARAM(p, "city") || "";
    const categoriesRaw =
      READ_PARAM(p, "categories", "service_categories") || "";
    const service_categories = categoriesRaw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    // If ANY business-specific param is present, default the role toggle
    // to "business" — cuts an extra click for CRM handoffs that always
    // come from the business side.
    const roleFromParams =
      business_name || address || phone || service_categories.length
        ? "business"
        : null;
    return { business_name, name, email, phone, address, service_categories, roleFromParams };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const [role, setRole] = useState(prefill.roleFromParams || "business");
  const [form, setForm] = useState({
    email: prefill.email,
    password: "",
    name: prefill.name,
    business_name: prefill.business_name,
    address: prefill.address,
    phone: prefill.phone,
    invite_code: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // If the user navigates to /register?email=... after the component mounted
  // (e.g. via a client-side link), sync the form state without wiping any
  // fields they've already customised.
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      email: prev.email || prefill.email,
      name: prev.name || prefill.name,
      business_name: prev.business_name || prefill.business_name,
      address: prev.address || prefill.address,
      phone: prev.phone || prefill.phone,
    }));
    if (prefill.roleFromParams && role !== prefill.roleFromParams) {
      setRole(prefill.roleFromParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const anyPrefilled =
    !!prefill.email ||
    !!prefill.name ||
    !!prefill.business_name ||
    !!prefill.address ||
    !!prefill.phone ||
    prefill.service_categories.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    // Only include `address`, `phone`, `invite_code`, and pre-filled
    // `service_categories` for business signups.
    const payload =
      role === "business"
        ? {
            ...form,
            role,
            phone: form.phone.trim() || undefined,
            invite_code: form.invite_code.trim() || undefined,
            service_categories: prefill.service_categories.length
              ? prefill.service_categories
              : undefined,
          }
        : { ...form, address: undefined, phone: undefined, invite_code: undefined, role };
    const r = await register(payload);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      toast.error(r.error);
      return;
    }
    toast.success("Account created");
    // Feb 2026: fresh business signups have `plan_selection_completed=false`
    // and no trial. Route them straight to the paywall so they pick a plan
    // and complete Stripe checkout before ever seeing the dashboard.
    navigate(r.user.role === "business" ? "/dashboard/subscribe" : "/");
  };

  return (
    <div className="mx-auto grid min-h-[calc(100vh-56px)] max-w-[1400px] grid-cols-1 md:grid-cols-2">
      <div className="hidden border-r border-border bg-grid p-12 md:block">
        <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
          → create account
        </p>
        <h2 className="mt-4 text-4xl leading-tight tracking-tight">
          Two sides.
          <br />
          One map.
        </h2>
        <div className="mt-8 grid gap-3 max-w-sm">
          <div className="border border-border p-4">
            <p className="mono text-[10px] uppercase tracking-widest text-[#C9A85F]">Business</p>
            <p className="mt-1 text-sm">Post openings. Approve or decline requests.</p>
          </div>
          <div className="border border-border p-4">
            <p className="mono text-[10px] uppercase tracking-widest text-[#C9A85F]">Consumer</p>
            <p className="mt-1 text-sm">Track your booking requests in one place.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            Sign up
          </p>
          <h1 className="mt-2 text-4xl leading-tight tracking-tight">
            Get started.
          </h1>

          {/* Pre-fill acknowledgement — shown when the register link brought
              details along (from a CRM handoff, marketing embed, or a
              referral). Confirms the visitor that we already have their
              basics so they only need to add a password + submit. */}
          {anyPrefilled && (
            <p
              data-testid="register-prefilled-banner"
              className="mono mt-4 border border-[#C9A85F] bg-[#FDF4D8] px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-[#A88A47]"
            >
              → we've pre-filled your details · just add a password
            </p>
          )}

          <div className="mt-6 grid grid-cols-2 gap-0 border border-border">
            {[
              { v: "business", label: "Business" },
              { v: "consumer", label: "Consumer" },
            ].map((r) => (
              <button
                key={r.v}
                type="button"
                data-testid={`role-${r.v}-btn`}
                onClick={() => setRole(r.v)}
                className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
                  role === r.v
                    ? "bg-foreground text-background"
                    : "hover:bg-muted"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="mt-6 grid gap-4">
            <div className="grid gap-1.5">
              <Label className="mono text-[10px] uppercase tracking-[0.2em]">Your name</Label>
              <Input
                data-testid="register-name-input"
                value={form.name}
                onChange={upd("name")}
                required
                className="rounded-none"
              />
            </div>
            {role === "business" && (
              <div className="grid gap-1.5">
                <Label className="mono text-[10px] uppercase tracking-[0.2em]">
                  Business name
                </Label>
                <Input
                  data-testid="register-business-name-input"
                  value={form.business_name}
                  onChange={upd("business_name")}
                  className="rounded-none"
                  placeholder="Optional; defaults to your name"
                />
              </div>
            )}
            {role === "business" && (
              <div className="grid gap-1.5">
                <Label className="mono text-[10px] uppercase tracking-[0.2em]">
                  Business address
                </Label>
                <Input
                  data-testid="register-address-input"
                  value={form.address}
                  onChange={upd("address")}
                  required
                  className="rounded-none"
                  placeholder="e.g. 500 Crown St, Surry Hills, Sydney"
                />
                <p className="mono text-[10px] text-muted-foreground">
                  We geocode this to place you on the map. Street + suburb + city works best.
                </p>
              </div>
            )}
            {role === "business" && (
              <div className="grid gap-1.5">
                <Label className="mono text-[10px] uppercase tracking-[0.2em]">
                  Mobile number
                </Label>
                <Input
                  data-testid="register-phone-input"
                  value={form.phone}
                  onChange={upd("phone")}
                  type="tel"
                  required
                  className="rounded-none"
                  placeholder="+61412345678"
                />
                <p className="mono text-[10px] text-muted-foreground">
                  International format (starts with +). Shown to customers
                  once you approve their booking.
                </p>
              </div>
            )}
            <div className="grid gap-1.5">
              <Label className="mono text-[10px] uppercase tracking-[0.2em]">Email</Label>
              <Input
                data-testid="register-email-input"
                value={form.email}
                onChange={upd("email")}
                type="email"
                required
                className="rounded-none"
              />
            </div>
            <div className="grid gap-1.5">
              <Label className="mono text-[10px] uppercase tracking-[0.2em]">Password</Label>
              <PasswordInput
                data-testid="register-password-input"
                value={form.password}
                onChange={upd("password")}
                required
                minLength={6}
                className="rounded-none"
              />
            </div>
            {role === "business" && (
              <div className="grid gap-1.5">
                <Label className="mono text-[10px] uppercase tracking-[0.2em]">
                  Foundation invite code <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  data-testid="register-invite-code-input"
                  value={form.invite_code}
                  onChange={upd("invite_code")}
                  placeholder="e.g. FOUNDERS2026"
                  className="rounded-none"
                  autoCapitalize="characters"
                />
                <p className="mono text-[10px] text-muted-foreground">
                  Got a code from us? Enter it here for 2 months free as a Foundation Member.
                </p>
              </div>
            )}
            {error && (
              <p data-testid="register-error" className="mono text-xs text-[#F87171]">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="register-submit-btn"
              disabled={busy}
              className="mt-2 w-full gold-metallic-bg px-4 py-3 text-sm font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform disabled:opacity-60"
            >
              {busy ? "Creating…" : "Create account"}
            </button>
            <p
              data-testid="register-terms-line"
              className="mt-3 text-[11px] text-muted-foreground leading-relaxed"
            >
              By creating an account you agree to our{" "}
              <Link to="/terms" className="underline hover:text-foreground">
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline hover:text-foreground">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          <Link
            to="/login"
            data-testid="register-to-login-link"
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl gold-metallic-bg px-6 py-4 text-base font-bold text-[#2A2724] shadow-[0_10px_24px_-12px_rgba(42,39,36,0.55)] transition-transform hover:-translate-y-0.5 active:scale-[0.98] md:text-lg"
          >
            Already have an account? Log in
          </Link>
        </form>
      </div>
    </div>
  );
}
